import DataTable, { DatatableInterface } from '@/components/data-table/DataTable';
import { Component, Ref, Vue } from 'vue-property-decorator';
import { Model as DataModel, SortOptions } from '@/models/Model';

@Component<AbstractDatatable>({})
export default class AbstractDatatable extends Vue implements DatatableInterface {

  @Ref()
  protected datatable!: DataTable;

  // #region RENAMED METHODS BECAUSE TOO COMMON OR LIFECYCLE HOOK

  public datatableMounted(): void {
    if (!this.datatable) { return; }
    this.datatable.mounted();
  }

  public datatableInitialize(): void {
    if (!this.datatable) { return; }
    this.datatable.initialize();
  }

  // #endregion

  public optionsChanged(): void {
    if (!this.datatable) { return; }
    this.datatable.optionsChanged();
  }

  public get searchDebounce(): Function {
    if (!this.datatable) { return () => {}; }
    return this.datatable.searchDebounce;
  }

  public get paginationDebounce(): Function {
    if (!this.datatable) { return () => {}; }
    return this.datatable.paginationDebounce;
  }

  public get selected(): any {
    if (!this.datatable) { return []; }
    return this.datatable.selected;
  }

  public get selectedFilter(): any {
    if (!this.datatable) { return []; }
    return this.datatable.selectedFilter;
  }

  public fetchSource(dispatchToStore?: boolean): void {
    if (!this.datatable) { return; }
    this.datatable.fetchSource(dispatchToStore);
  }

  public createDateModals(): void {
    if (!this.datatable) { return; }
    this.datatable.createDateModals();
  }

  public select(items: any[]): void {
    if (!this.datatable) { return; }
    this.datatable.select(items);
  }

  public unselect(items: any[]): void {
    if (!this.datatable) { return; }
    this.datatable.unselect(items);
  }

  public unselectAll(): void {
    if (!this.datatable) { return; }
    this.datatable.unselectAll();
  }

  public refresh(dispatchToStore?: boolean): void {
    if (!this.datatable) { return; }
    this.datatable.refresh(dispatchToStore);
  }

  public deleteItem(item: DataModel): void {
    if (!this.datatable) { return; }
    this.datatable.deleteItem(item);
  }

  public toggleAll(): void {
    if (!this.datatable) { return; }
    this.datatable.toggleAll();
  }

  public handlePagination(): Function {
    if (!this.datatable) { return () => {}; }
    return this.datatable.handlePagination();
  }

  public handleAction(action: Function, item: DataModel, props: any): void {
    if (!this.datatable) { return; }
    this.datatable.handleAction(action, item, props);
  }

  public handleIcon(action: Function, item: DataModel): void {
    if (!this.datatable) { return; }
    this.datatable.handleIcon(action, item);
  }

  public setItemChanged(item: DataModel): void {
    if (!this.datatable) { return; }
    this.datatable.setItemChanged(item);
  }

  public resetDateField(item: DataModel, property: string): void {
    if (!this.datatable) { return; }
    this.datatable.resetDateField(item, property);
  }

  public cancelEdit(): void {
    if (!this.datatable) { return; }
    this.datatable.cancelEdit();
  }

  public handleTableEdit(): void {
    if (!this.datatable) { return; }
    this.datatable.handleTableEdit();
  }

  public minDate(header: any): any {
    if (!this.datatable) { return; }
    return this.datatable.minDate(header);
  }

  public maxDate(header: any): any {
    if (!this.datatable) { return; }
    return this.datatable.maxDate(header);
  }

  public handleSearch(): any {
    if (!this.datatable) { return; }
    return this.datatable.handleSearch();
  }

  public createTitle(header: any): string {
    if (!this.datatable) { return ''; }
    return this.datatable.createTitle(header);
  }

  public createItemCache(): void {
    if (!this.datatable) { return; }
    this.datatable.createItemCache();
  }

  public update(): void {
    if (!this.datatable) { return; }
    this.datatable.update();
  }

  public visibleForRole(roles: string[]): boolean {
    if (!this.datatable) { return false; }
    return this.datatable.visibleForRole(roles);
  }

  public clearChangedItems(): void {
    if (!this.datatable) { return; }
    this.datatable.clearChangedItems();
  }

  public clearItemCache(): void {
    if (!this.datatable) { return; }
    this.datatable.clearItemCache();
  }

  public isFunction(object: any): boolean {
    if (!this.datatable) { return false; }
    return this.datatable.isFunction(object);
  }

  public changeSorting(sortable: SortOptions): void {
    if (!this.datatable) { return; }
    this.datatable.changeSorting(sortable);
  }

  public isSortable(header: any): boolean {
    if (!this.datatable) { return false; }
    return this.datatable.isSortable(header);
  }

  public getExtraClass(object: any): string {
    if (!this.datatable) { return ''; }
    return this.datatable.getExtraClass(object);
  }

  public selectionChange(value: any, oldValue: any): void {
    if (!this.datatable) { return; }
    this.datatable.selectionChange(value, oldValue);
  }

  public selectedFilterChange(value: any, oldValue: string): void {
    if (!this.datatable) { return; }
    this.datatable.selectedFilterChange(value, oldValue);
  }

  public limitChanged(limit: number, oldLimit: number): void {
    if (!this.datatable) { return; }
    this.datatable.limitChanged(limit, oldLimit);
  }

  public editModeToggled(isEditting: boolean): void {
    if (!this.datatable) { return; }
    this.datatable.editModeToggled(isEditting);
  }

  public clearSelectBoxes(): void {
    if (!this.datatable) { return; }
    this.datatable.clearSelectBoxes();
  }

  public headerClasses(header: any): any {
    if (!this.datatable) { return; }
    return this.datatable.headerClasses(header);
  }

  public columnStyles(header: any): any {
    if (!this.datatable) { return; }
    return this.datatable.columnStyles(header);
  }

  public addExpandedItem(props: any): void {
    if (!this.datatable) { return; }
    this.datatable.addExpandedItem(props);
  }

  public removeExpandedItem(props: any): void {
    if (!this.datatable) { return; }
    this.datatable.removeExpandedItem(props);
  }

  public isExpanded(item: DataModel): boolean {
    if (!this.datatable) { return false; }
    return this.datatable.isExpanded(item);
  }
}
